import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MachineContext } from '../DataContexts/MachineContext';
import { MachineGroupContext } from '../DataContexts/MachineGroupContext';
import DashboardSection from '../components/DashboardSection';
import { OVEREENKOMST_ORDER, getOvereenkomstLabel } from './accountsShared';
import '../PlaylistManager/PlaylistAssign.css';
import './AccountActions.css';

export const SECTION_ANCHOR = 'section-actions';
export const GIFT_AMOUNT = 10;
export const SCOPE_ALL = 'all';
export const SCOPE_GROUP = 'group';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Same countdown the per-machine Tokens block shows (DJB-29): the backend
// resets every machine to its plan default on the first of the month.
export const getDaysUntilNextMonth = (now = new Date()) => {
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return Math.ceil((nextMonth - now) / (1000 * 60 * 60 * 24));
};

// Plan defaults as "Basic 20 · Pro 50 · …" in the fixed plan order; the
// /token-config answer is a list of { overeenkomstType, defaultTokens }.
export const formatPlanDefaults = (tokenConfigs) => {
  if (!Array.isArray(tokenConfigs) || tokenConfigs.length === 0) return '';
  const byType = {};
  tokenConfigs.forEach((config) => {
    if (config?.overeenkomstType) byType[config.overeenkomstType] = config.defaultTokens;
  });
  const ordered = [
    ...OVEREENKOMST_ORDER.filter((type) => type in byType),
    ...Object.keys(byType).filter((type) => !OVEREENKOMST_ORDER.includes(type)),
  ];
  return ordered.map((type) => `${getOvereenkomstLabel(type)} ${byType[type] ?? '—'}`).join(' · ');
};

// DJB-210: Accounts › Settings › Actions. Fleet-wide actions applied to all
// machines at once or to one machine group. The scope selector at the top
// drives every action in the section; the first (and so far only) action is
// the Tokens row, the fleet/group twin of the per-machine "Reset now" / "+10"
// buttons (DJB-29):
//
// - Reset now: every machine in scope goes back to the default of its own
//   plan (POST /machines/tokens/reset, or /machines/groups/{id}/tokens/reset).
// - +10: gifts ten tokens on top of each machine's current balance
//   (POST /machines/tokens/gift/10, or /machines/groups/{id}/tokens/gift/10).
//
// Both run through MachineContext (DJB-Server PR #63). The backend applies
// the action in one transaction and answers with the machines it touched plus
// any group members that no longer exist (skipped); the outcome is rendered
// inline. Because a click touches many machines, both buttons confirm first.
const AccountActions = ({ active = true, onToggle }) => {
  const { machines, bulkResetTokens, bulkGiftTokens, fetchTokenConfigs } = useContext(MachineContext);
  const { machineGroups, loading: groupsLoading, error: groupsError } = useContext(MachineGroupContext);

  const [scope, setScope] = useState(SCOPE_ALL);
  const [scopeGroupId, setScopeGroupId] = useState('');
  const [tokenConfigs, setTokenConfigs] = useState(null);
  // null | { action: 'reset' | 'gift' } while the confirm dialog is open
  const [confirming, setConfirming] = useState(null);
  // null | { status: 'running' | 'done' | 'failed', action, scopeLabel,
  //          machineCount, affectedMachines, skippedMachines, message }
  const [run, setRun] = useState(null);
  const running = run?.status === 'running';

  // Plan defaults for the info line, loaded once the section is open
  const fetchConfigsRef = useRef(fetchTokenConfigs);
  fetchConfigsRef.current = fetchTokenConfigs;
  useEffect(() => {
    if (!active || tokenConfigs !== null) return;
    let cancelled = false;
    Promise.resolve(fetchConfigsRef.current?.())
      .then((configs) => {
        if (!cancelled) setTokenConfigs(Array.isArray(configs) ? configs : []);
      })
      .catch(() => {
        if (!cancelled) setTokenConfigs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [active, tokenConfigs]);

  const selectedGroup = useMemo(
    () =>
      scope === SCOPE_GROUP && scopeGroupId
        ? (machineGroups?.find((g) => g.id === scopeGroupId) ?? null)
        : null,
    [scope, scopeGroupId, machineGroups],
  );

  const fleetCount = machines?.length ?? 0;
  const groupCount = selectedGroup?.machineIds?.length ?? 0;
  const scopeCount = scope === SCOPE_ALL ? fleetCount : groupCount;
  const scopeLabel = scope === SCOPE_ALL ? 'all machines' : `group ${selectedGroup?.name ?? ''}`;
  // the backend scopes the request itself; groupId only travels for a group
  const scopeGroupIdForRequest = scope === SCOPE_GROUP ? (selectedGroup?.id ?? null) : null;

  const scopeReady = scope === SCOPE_ALL || Boolean(selectedGroup);
  const canAct = scopeReady && scopeCount > 0 && !running;
  const disabledHint = !scopeReady
    ? 'Pick a machine group first.'
    : scopeCount === 0
      ? scope === SCOPE_ALL
        ? 'There are no machines yet.'
        : 'This group has no machines.'
      : null;

  const selectScope = (nextScope) => {
    if (running) return;
    setScope(nextScope);
    setRun(null);
  };

  const selectGroup = (groupId) => {
    if (running) return;
    setScopeGroupId(groupId);
    setRun(null);
  };

  const actionVerb = (action, tense) => {
    if (action === 'reset') return tense === 'running' ? 'Resetting tokens' : 'Reset tokens';
    return tense === 'running' ? `Gifting +${GIFT_AMOUNT} tokens` : `Gifted +${GIFT_AMOUNT} tokens`;
  };

  const runAction = async (action) => {
    if (!canAct) return;
    setConfirming(null);
    const base = {
      status: 'running',
      action,
      scopeLabel,
      machineCount: scopeCount,
      affectedMachines: 0,
      skippedMachines: 0,
      message: null,
    };
    setRun(base);
    try {
      const result =
        action === 'reset'
          ? await bulkResetTokens(scopeGroupIdForRequest, { notify: false })
          : await bulkGiftTokens(GIFT_AMOUNT, scopeGroupIdForRequest, {
              notify: false,
            });
      setRun({
        ...base,
        status: 'done',
        affectedMachines: result?.affectedMachines ?? result?.machineIds?.length ?? 0,
        skippedMachines: result?.skippedMachines ?? result?.skippedMachineIds?.length ?? 0,
      });
    } catch (e) {
      setRun({ ...base, status: 'failed', message: e?.message ?? null });
    }
  };

  const planDefaults = formatPlanDefaults(tokenConfigs);
  const groupOptions = machineGroups ?? [];

  return (
    <DashboardSection
      id={SECTION_ANCHOR}
      title="Actions"
      info="Run one action on all machines at once or on a machine group — first up: Reset Now / +10 tokens."
      isOpen={active}
      onToggle={onToggle}
      bodyId="aa-body-actions"
    >
      <div className="ps-page account-actions-page">
        {/* Scope: every action below applies to this scope */}
        <div className="aa-scope">
          <div className="aa-scope-head">
            <span className="aa-scope-label" id="aa-scope-label">
              Apply to
            </span>
            <span className="aa-scope-count">{plural(scopeCount, 'machine')}</span>
          </div>
          <div className="aa-scope-row">
            <div className="aa-scope-options" role="radiogroup" aria-labelledby="aa-scope-label">
              <label className={`aa-scope-option${scope === SCOPE_ALL ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="aa-scope"
                  value={SCOPE_ALL}
                  checked={scope === SCOPE_ALL}
                  disabled={running}
                  onChange={() => selectScope(SCOPE_ALL)}
                />
                <span className="aa-scope-option-text">All machines</span>
                <span className="aa-scope-badge">{fleetCount}</span>
              </label>
              <label className={`aa-scope-option${scope === SCOPE_GROUP ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="aa-scope"
                  value={SCOPE_GROUP}
                  checked={scope === SCOPE_GROUP}
                  disabled={running}
                  onChange={() => selectScope(SCOPE_GROUP)}
                />
                <span className="aa-scope-option-text">Machine group</span>
                <span className="aa-scope-badge">{groupOptions.length}</span>
              </label>
            </div>
            {scope === SCOPE_GROUP && (
              <select
                className="ps-select aa-group-select"
                aria-label="Machine group"
                value={scopeGroupId}
                disabled={running || groupsLoading}
                onChange={(e) => selectGroup(e.target.value)}
              >
                <option value="">
                  {groupsLoading ? 'Loading groups…' : groupOptions.length ? 'Pick a group…' : 'No groups'}
                </option>
                {groupOptions.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({plural(group.machineIds?.length ?? 0, 'machine')})
                  </option>
                ))}
              </select>
            )}
          </div>
          {groupsError && (
            <p className="ps-status ps-status--error" role="alert">
              Error loading groups: {groupsError}
            </p>
          )}
        </div>

        {/* Actions: one row per action, the scope above applies to all of them */}
        <ul className="aa-action-list">
          <li className="aa-action aa-action--tokens">
            <div className="aa-action-head">
              <span className="aa-action-title">Tokens</span>
              <span className="aa-action-kind">Reset now · +{GIFT_AMOUNT}</span>
            </div>
            <p className="aa-action-text">
              <strong>Reset now</strong> puts every machine in scope back to the default of its own plan.{' '}
              <strong>+{GIFT_AMOUNT}</strong> gifts {GIFT_AMOUNT} tokens on top of each machine&apos;s current
              balance. The jukeboxes pick the new balance up right away.
            </p>
            <p className="aa-token-info">
              tokens will be automatically reset in {getDaysUntilNextMonth()} days
              {planDefaults ? ` (${planDefaults})` : ''}
            </p>
            <div className="aa-token-actions">
              <button
                type="button"
                className="aa-token-btn aa-token-reset-btn"
                disabled={!canAct}
                title={disabledHint ?? `Reset the tokens of ${scopeLabel} to their plan default`}
                onClick={() => setConfirming({ action: 'reset' })}
              >
                {running && run.action === 'reset' ? 'Resetting…' : 'Reset now'}
              </button>
              <button
                type="button"
                className="aa-token-btn aa-token-add-btn"
                disabled={!canAct}
                title={disabledHint ?? `Gift ${GIFT_AMOUNT} tokens to ${scopeLabel}`}
                onClick={() => setConfirming({ action: 'gift' })}
              >
                {running && run.action === 'gift' ? 'Adding…' : `+${GIFT_AMOUNT}`}
              </button>
              {disabledHint && <span className="aa-token-hint">{disabledHint}</span>}
            </div>

            {run && (
              <div className={`aa-result aa-result--${run.status}`} role="status">
                {run.status === 'running' && (
                  <p className="aa-result-text">
                    {actionVerb(run.action, 'running')} on {plural(run.machineCount, 'machine')} of{' '}
                    {run.scopeLabel}…
                  </p>
                )}
                {run.status === 'done' && (
                  <>
                    <p className="aa-result-text">
                      {actionVerb(run.action, 'done')} on {plural(run.affectedMachines, 'machine')} of{' '}
                      {run.scopeLabel}.
                    </p>
                    {run.skippedMachines > 0 && (
                      <p className="aa-result-text aa-result-text--skipped">
                        {plural(run.skippedMachines, 'machine')} skipped: listed in the group but no longer
                        existing.
                      </p>
                    )}
                  </>
                )}
                {run.status === 'failed' && (
                  <p className="aa-result-text aa-result-text--error">
                    Could not {run.action === 'reset' ? 'reset tokens' : `gift +${GIFT_AMOUNT} tokens`} on{' '}
                    {run.scopeLabel}
                    {run.message ? ` — ${run.message}` : ''}. Nothing was changed.
                  </p>
                )}
              </div>
            )}
          </li>
        </ul>

        {/* inside .ps-page so the dialog picks up the ps-* design tokens */}
        {confirming && (
          <div className="ps-confirm-overlay" role="presentation">
            <div
              className="ps-confirm-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="aa-confirm-title"
            >
              <p id="aa-confirm-title">
                {confirming.action === 'reset' ? (
                  <>
                    Reset the tokens of <strong>{plural(scopeCount, 'machine')}</strong> of {scopeLabel} to
                    their plan default?
                  </>
                ) : (
                  <>
                    Gift <strong>+{GIFT_AMOUNT} tokens</strong> to{' '}
                    <strong>{plural(scopeCount, 'machine')}</strong> of {scopeLabel}?
                  </>
                )}
              </p>
              <p className="aa-confirm-note">
                {confirming.action === 'reset'
                  ? 'Every one of those machines loses its current balance and starts over from its plan default.'
                  : 'Every one of those machines gets the tokens on top of its current balance.'}
              </p>
              <div className="ps-confirm-actions">
                <button type="button" className="ps-btn ps-btn--ghost" onClick={() => setConfirming(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`ps-btn ${confirming.action === 'reset' ? 'ps-btn--danger' : 'ps-btn--success'}`}
                  onClick={() => runAction(confirming.action)}
                >
                  {confirming.action === 'reset' ? 'Reset now' : `+${GIFT_AMOUNT}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardSection>
  );
};

export default AccountActions;
